import type { CoverableInformationToUpdateDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa';
import type { YesNo_Top } from 'gw-edge-client/dist/types/dto/pc/typekey';
import WizardPagetemplate from '../../../templates/WizardPagetemplate';
import { useCallback, useState } from 'react';
import CallMeModal from '../../../shared_components/CallMeModal/CallMeModal';
import usePageNavigation from '../../../hooks/usePageNavigation';
import { useRecoilValue } from 'recoil';
import { opcInfoAtom } from '../../../recoil/atoms/opcInfoAtom';
import { FLAVOUR_NAME_MAP } from '../../../constants/coverable';
import PetHealthyLastSixMonths from '../../../shared_components/PetHealthyLastSixMonths/PetHealthyLastSixMonths';
import { useHippoText } from '../../../hooks/useHippoText';
import CoverableDetailsTemplate from '../../../templates/CoverableDetailsTemplate';
import { getCoverableInfoSelector } from '../../../recoil/selectors/getCoverableInfoSelector';
import useOpcJob from '../../../hooks/useOpcJob';

const PetHealthyLastSixMonthsOPC = ({
    continueToOpc
}: {
    continueToOpc: (petHealthInfo: CoverableInformationToUpdateDTO) => void;
}): React.JSX.Element => {
    const getHippoText = useHippoText();
    const [healthValue, setHealthValue] = useState<YesNo_Top>('Yes');
    const { goBackToDetailsPage } = usePageNavigation();
    const { withdrawCurrentOPCShoppingBasketIfNotExisting } = useOpcJob();
    const [showCallMe, setShowCallMe] = useState(false);
    const { coverableId, productName } = useRecoilValue(opcInfoAtom);
    const coverableInfo = useRecoilValue(getCoverableInfoSelector);

    const getUpdateInfo = useCallback((): CoverableInformationToUpdateDTO => {
        const updatedPet = {
            isHealthyFor6Months: healthValue,
            fixedId: coverableId,
            publicId: coverableId
        };

        const updateCoverableInfoDTO: CoverableInformationToUpdateDTO = {
            productToUpdate: FLAVOUR_NAME_MAP[productName],
            ...(productName === 'topdanmark_dog' ? { dog: updatedPet } : {}),
            ...(productName === 'topdanmark_cat' ? { cat: updatedPet } : {})
        };
        return updateCoverableInfoDTO;
    }, [coverableId, healthValue, productName]);

    const handleOnNext = (): void => {
        switch (healthValue) {
            case 'Yes':
                continueToOpc(getUpdateInfo());
                break;
            case 'No':
                setShowCallMe(true);
                break;
            default:
                throw new Error('Unknown health value');
        }
    };

    const handleCloseConfirmationModal = async (): Promise<void> => {
        await withdrawCurrentOPCShoppingBasketIfNotExisting();
        goBackToDetailsPage();
    };

    return (
        <WizardPagetemplate onNext={handleOnNext} onPrevious={goBackToDetailsPage}>
            <CoverableDetailsTemplate
                header={getHippoText('DSS-PET-COVERABLE-INFORMATION-SIX-MONTHS-HEALTHY-QUESTION', {
                    navn: coverableInfo?.petName ?? ''
                })}
            >
                <PetHealthyLastSixMonths
                    healthValue={healthValue}
                    handleHealthValueChange={(e) => {
                        const { value } = e.target;
                        setHealthValue(value as YesNo_Top);
                    }}
                />
            </CoverableDetailsTemplate>
            {showCallMe && (
                <CallMeModal
                    activityCode="dss_call_request"
                    isDefault={false}
                    title={getHippoText('DSS-CALL-ME-MODAL-PET-NOT-HEALTHY-HEADER')}
                    description={getHippoText('DSS-CALL-ME-MODAL-PET-NOT-HEALTHY-SUBHEADER')}
                    showModal={showCallMe}
                    closeModal={() => {
                        setShowCallMe(false);
                    }}
                    closeConfirmationModal={() => {
                        void handleCloseConfirmationModal();
                    }}
                    overlapseModalText={{
                        // TODO: Use hippo label when label id is re-define in document [Hippo PET]
                        modalChooseTimeSubHeader: 'Vælg et tidspunkt, hvor vi kan ringe dig op.',
                        modalContactedPrompt: 'Hvornår skal vi ringe?'
                    }}
                />
            )}
        </WizardPagetemplate>
    );
};

export default PetHealthyLastSixMonthsOPC;
